// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { AvailableMember } from '@/components/PlayerMembershipEditor';
import type { FriendSnapshot } from '@/services/bffWebSocket';
import type { AvailablePlayer, Player } from './types';

const MAX_PLAYERS = 8;

// Bounds match the handicap editor (PlayerEditModal), so a generated value is one the user could
// have typed themselves.
const MIN_GHOST_HANDICAP = -10;
const MAX_GHOST_HANDICAP = 54;
const FALLBACK_GHOST_HANDICAP = 18;
const GHOST_HANDICAP_SPREAD = 3;

/**
 * A ghost of someone with no handicap on file would otherwise start the round with none at all.
 * Put it among the humans instead: the average of their playing handicaps, nudged by up to three
 * strokes either way so two ghosts in one round are not clones. With nobody to average — an empty
 * round, or nobody has entered a handicap yet — it falls back to a mid handicap.
 */
export const randomGhostHandicap = (
  realHandicaps: Array<string | undefined>,
  random: () => number = Math.random
): string => {
  const values = realHandicaps
    .map((value) => (value ?? '').trim().replace(',', '.'))
    .filter((value) => value.length > 0)
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
  const base =
    values.length > 0
      ? values.reduce((sum, value) => sum + value, 0) / values.length
      : FALLBACK_GHOST_HANDICAP;
  const nudged = Math.round(base) + Math.round((random() * 2 - 1) * GHOST_HANDICAP_SPREAD);
  return String(Math.min(MAX_GHOST_HANDICAP, Math.max(MIN_GHOST_HANDICAP, nudged)));
};

/**
 * The playing handicap a ghost starts on. Any handicap actually known for the player it copies
 * wins — the caller passes them most-recent-first — and only a player nobody has a number for
 * gets an invented one that fits the humans in the round.
 */
export const pickGhostHandicap = (
  knownHandicaps: Array<string | null | undefined>,
  realHandicaps: Array<string | undefined>,
  random: () => number = Math.random
): string => {
  const known = knownHandicaps
    .map((value) => (value ?? '').trim())
    .find((value) => value.length > 0);
  return known ?? randomGhostHandicap(realHandicaps, random);
};

// Candidate pool for the add-player pickers: the caller's friends, the guests they created
// themselves, and any registered player already loaded. Guests belonging to somebody else are
// deliberately left out — the pickers narrow this further via buildMembershipCandidatePool.
export const buildAvailableMembers = (
  friendDirectory: FriendSnapshot[] | undefined,
  availablePlayers: AvailablePlayer[]
): AvailableMember[] => {
  const merged = new Map<string, AvailableMember>();

  (friendDirectory ?? []).forEach((friend) => {
    if (!friend.playerId) return;
    const name = (friend.playerName ?? friend.name ?? '').trim();
    if (!name) return;
    merged.set(friend.playerId, {
      id: friend.playerId,
      name,
      userId: friend.userId ?? null,
      isFriend: true,
    });
  });

  availablePlayers
    // AI players are added through the dedicated AI picker, never the normal pool.
    .filter((player) => player.isAi !== true)
    .filter((player) => Boolean(player.userId) || player.isMine === true)
    .forEach((player) => {
      if (merged.has(player.id)) return;
      merged.set(player.id, {
        id: player.id,
        name: player.name,
        userId: player.userId ?? null,
        isFriend: player.isFriend === true,
        isMine: player.isMine === true,
        createdAt: player.createdAt,
      });
    });

  return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export const normalizeDebugParam = (debugParam?: string | string[]) => {
  const values = (Array.isArray(debugParam) ? debugParam : [debugParam])
    .map((value) => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
    .filter((value) => value.length > 0);

  if (values.includes('false')) {
    return false;
  }

  return values.includes('true');
};

export const deriveInitialTourId = (tourParam: string | string[] | undefined, storedTourId: string | null) => {
  const raw = Array.isArray(tourParam) ? tourParam[0] : tourParam;
  const candidate = raw?.trim() || storedTourId;
  if (!candidate) return null;
  if (candidate === '00000000-0000-0000-0000-000000000000') return null;
  if (candidate === 'single-round') return null;
  return candidate;
};

export const buildPlayerOrder = (
  incomingPlayers: Player[],
  currentUserPlayer: AvailablePlayer | undefined,
  createId: () => string
) => {
  const orderedPlayers: Player[] = [];
  if (currentUserPlayer) {
    const incomingSelf = incomingPlayers.find((player) => player.id === currentUserPlayer.id);
    orderedPlayers.push({
      id: currentUserPlayer.id,
      name: currentUserPlayer.name,
      scp: incomingSelf?.scp ?? '',
      scpSource: incomingSelf?.scpSource,
    });
  }
  incomingPlayers.forEach((player) => {
    if (currentUserPlayer && player.id === currentUserPlayer.id) return;
    orderedPlayers.push(player);
  });
  return orderedPlayers.length ? orderedPlayers.slice(0, MAX_PLAYERS) : [{ id: createId(), name: '', scp: '' }];
};

export const buildEmptyScpIds = (players: Player[], availablePlayerIdSet: Set<string>) =>
  players
    .filter((player) => !player.scp.trim() && availablePlayerIdSet.has(player.id))
    .map((player) => player.id)
    .sort();

export const upsertAvailablePlayer = (
  availablePlayers: AvailablePlayer[],
  incomingPlayer: AvailablePlayer
) => {
  const incomingId = incomingPlayer.id.trim();
  if (!incomingId) {
    return availablePlayers;
  }

  const resolvedName = incomingPlayer.name.trim() || incomingId;
  const nextPlayer: AvailablePlayer = {
    id: incomingId,
    name: resolvedName,
    userId: incomingPlayer.userId ?? null,
    isTemporary: incomingPlayer.isTemporary ?? incomingPlayer.userId == null,
    isFriend: incomingPlayer.isFriend ?? false,
    ...(incomingPlayer.isMine !== undefined ? { isMine: incomingPlayer.isMine } : {}),
    ...(incomingPlayer.isAi !== undefined ? { isAi: incomingPlayer.isAi } : {}),
    ...(incomingPlayer.createdAt !== undefined ? { createdAt: incomingPlayer.createdAt } : {}),
  };

  let updated = false;
  const merged = availablePlayers.map((player) => {
    if (player.id !== incomingId) {
      return player;
    }
    updated = true;
    return {
      ...player,
      ...nextPlayer,
    };
  });

  const withPlayer = updated ? merged : [...merged, nextPlayer];
  return [...withPlayer].sort((left, right) => {
    const leftFriend = left.isFriend === true;
    const rightFriend = right.isFriend === true;
    if (leftFriend !== rightFriend) {
      return leftFriend ? -1 : 1;
    }
    const byName = left.name.localeCompare(right.name);
    if (byName !== 0) {
      return byName;
    }
    return left.id.localeCompare(right.id);
  });
};

export const appendPlayerRowIfMissing = (
  players: Player[],
  incomingPlayer: Player,
  maxPlayers: number = MAX_PLAYERS
) => {
  const incomingId = incomingPlayer.id.trim();
  if (!incomingId) {
    return players;
  }

  const existing = players.find((player) => player.id === incomingId);
  if (existing) {
    return players.map((player) =>
      player.id === incomingId
        ? {
            ...player,
            name: incomingPlayer.name.trim() || player.name,
          }
        : player
    );
  }

  if (players.length >= maxPlayers) {
    return players;
  }

  return [
    ...players,
    {
      id: incomingId,
      name: incomingPlayer.name.trim() || incomingId,
      scp: incomingPlayer.scp ?? '',
      scpSource: incomingPlayer.scpSource,
      isAi: incomingPlayer.isAi,
      aiKind: incomingPlayer.aiKind,
      aiSourcePlayerId: incomingPlayer.aiSourcePlayerId,
    },
  ];
};
